import { createAction, createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  AuthorizationUserPayload,
  AuthState,
  FetchPaymentStatusPayload,
  FetchRelatedBrandsPayload,
  ForgotPasswordPayload,
  LoginPayload,
  OAuthPayload,
  PaymentStatus,
  RegisterPayload,
  ResetPasswordPayload,
  Role,
  UpdatePasswordPayload,
  UpdateUserPayload,
} from "./types";
import { User } from "../../types/auth";
import { BrandRegisterPayload, LogoResponse } from "../brands/types";
import { Company } from "./types";
import { CompetitorWithLogo } from "../../types/competitor";
import { DEFAULT_PLAN, SubscriptionPlan } from "../../constants/plans";

/* ── sessionStorage persistence for actingAs (survives hard reload) ── */
const ACTING_AS_KEY = "aicarma_acting_as";

function loadActingAs(): { clientId: string; clientAccountId?: string; label: string | null; email: string | null } | null {
  try {
    const raw = sessionStorage.getItem(ACTING_AS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed.clientId === "string") return parsed;
  } catch { /* ignore */ }
  return null;
}

function saveActingAs(clientId: string, clientAccountId: string | null, label: string | null, email: string | null): void {
  try {
    sessionStorage.setItem(ACTING_AS_KEY, JSON.stringify({ clientId, clientAccountId, label, email }));
  } catch { /* ignore */ }
}

function clearActingAsStorage(): void {
  try {
    sessionStorage.removeItem(ACTING_AS_KEY);
  } catch { /* ignore */ }
}

const restoredActingAs = loadActingAs();

export const initialState: AuthState = {
  user: null,
  role: "user",
  isRecovery: false,
  loading: false,
  loadingUpdate: false,
  isTrial: false,
  isMember: false,
  isAgency: false,
  isManagedClient: false,
  accountId: null,
  actingAsClientId: restoredActingAs?.clientId ?? null,
  actingAsAccountId: restoredActingAs?.clientAccountId ?? null,
  actingAsLabel: restoredActingAs?.label ?? null,
  actingAsEmail: restoredActingAs?.email ?? null,
  paymentStatus: "unknown",
  errorRegister: null,
  errorLogin: null,
  errorResetPassword: null,
  successResetPassword: false,
  subscriptionPlan: null,
  accountRegister: {
    plan: DEFAULT_PLAN,
    company: null,
    loadingCompetitors: false,
    loadingRelatedBrands: false,
    loadingBrandRegister: false,
    loading: false,
    step: 1,
    relatedBrands: null,
    competitors: null,
    selectedCompetitors: null,
  },
};

const authSlice = createSlice({
  name: "authUser",
  initialState,
  reducers: {
    setPlan: (state, action: PayloadAction<SubscriptionPlan>) => {
      state.accountRegister.plan = action.payload;
    },
    setStepRegister: (state, action: PayloadAction<number>) => {
      state.accountRegister.step = action.payload;
    },
    setBrand: (state, action: PayloadAction<Company>) => {
      state.accountRegister.company = action.payload;
    },
    setCompetitorsRegister: (state, action: PayloadAction<CompetitorWithLogo[]>) => {
      state.accountRegister.competitors = action.payload;
    },
    setSelectedCompetitorsRegister: (
      state,
      action: PayloadAction<CompetitorWithLogo[]>,
    ) => {
      state.accountRegister.selectedCompetitors = action.payload;
    },
    setCompetitorsRegisterLoading: (state) => {
      state.accountRegister.loadingCompetitors = true;
    },
    setBrandRegisterLoading: (state) => {
      state.accountRegister.loadingBrandRegister = true;
    },
    setBrandRegisterSuccess: (state) => {
      state.accountRegister.loadingBrandRegister = false;
    },
    setBrandRegisterFailed: (state) => {
      state.accountRegister.loadingBrandRegister = false;
    },
    fetchCompetitorsRegisterSuccess: (
      state,
      action: PayloadAction<CompetitorWithLogo[]>,
    ) => {
      state.accountRegister.loadingCompetitors = false;
      state.accountRegister.competitors = action.payload;
    },
    fetchCompetitorsRegisterFailed: (state) => {
      state.accountRegister.loadingCompetitors = false;
    },
    setRegisterUserLoading: (state) => {
      state.accountRegister.loading = true;
    },
    registerUserSuccess: (state) => {
      state.accountRegister.loading = false;
      state.errorRegister = null;
    },
    registerUserFailed: (state, action: PayloadAction<string>) => {
      state.accountRegister.loading = false;
      state.errorRegister = action.payload;
    },

    setLoginUserLoading: (state) => {
      state.loading = true;
    },
    setUpdateUserLoading: (state) => {
      state.loadingUpdate = true;
    },
    setUser: (state, action: PayloadAction<User>) => {
      state.user = action.payload;
    },
    loginUserSuccess: (state) => {
      state.loading = false;
      state.errorLogin = null;
    },
    loginUserFailed: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.errorLogin = action.payload;
    },

    setPaymentStatusLoading: (state) => {
      state.paymentStatus = "pending";
    },

    fetchPaymentStatusSuccess: (
      state,
      action: PayloadAction<PaymentStatus>,
    ) => {
      state.paymentStatus = action.payload;
    },
    fetchPaymentStatusFailed: (state) => {
      state.paymentStatus = "unknown";
    },

    logoutUserSuccess: (state) => {
      state.user = null;
      state.actingAsClientId = null;
      state.actingAsAccountId = null;
      state.actingAsLabel = null;
      state.actingAsEmail = null;
      clearActingAsStorage();
    },

    resetPasswordUserSuccess: (state) => {
      state.successResetPassword = true;
      state.errorResetPassword = null;
    },
    resetPasswordUserFailed: (state) => {
      state.loading = false;
      state.errorResetPassword = "Failed to reset password";
    },
    setResetPasswordUserLoading: (state) => {
      state.loading = true;
    },
    updatePasswordUserSuccess: (state) => {
      state.loadingUpdate = false;
      state.errorResetPassword = null;
    },
    updatePasswordUserFailed: (state) => {
      state.loadingUpdate = false;
      state.errorResetPassword = "Failed to update password";
    },

    updateUserSuccess: (state) => {
      state.loadingUpdate = false;
    },
    updateUserFailed: (state) => {
      state.loadingUpdate = false;
    },

    setRecovery: (state, action: PayloadAction<boolean>) => {
      state.isRecovery = action.payload;
    },

    setRelatedBrandsLoading: (state) => {
      state.accountRegister.loadingRelatedBrands = true;
    },
    fetchRelatedBrandsSuccess: (
      state,
      action: PayloadAction<LogoResponse[]>,
    ) => {
      state.accountRegister.loadingRelatedBrands = false;
      state.accountRegister.relatedBrands = action.payload;
    },
    resetRelatedBrands: (state) => {
      state.accountRegister.relatedBrands = null;
    },
    fetchRelatedBrandsFailed: (state) => {
      state.accountRegister.loadingRelatedBrands = false;
    },
    setMemberInfo: (state, action: PayloadAction<{ isMember: boolean; isManagedClient: boolean; accountId: string | null }>) => {
      state.isMember = action.payload.isMember;
      state.isManagedClient = action.payload.isManagedClient;
      state.accountId = action.payload.accountId;
    },
    clearActingAsOnUserChange: (
      state,
      action: PayloadAction<{ previousUserId: string | null; userId: string }>,
    ) => {
      const { previousUserId, userId } = action.payload;
      if (previousUserId && previousUserId !== userId) {
        state.actingAsClientId = null;
        state.actingAsAccountId = null;
        state.actingAsLabel = null;
        state.actingAsEmail = null;
        clearActingAsStorage();
      }
    },
    setAgencyInfo: (state, action: PayloadAction<{ isAgency: boolean }>) => {
      state.isAgency = action.payload.isAgency;
    },
    setActingAs: (state, action: PayloadAction<{ clientId: string; clientAccountId?: string; label: string | null; email?: string | null }>) => {
      state.actingAsClientId = action.payload.clientId;
      state.actingAsAccountId = action.payload.clientAccountId ?? null;
      state.actingAsLabel = action.payload.label;
      state.actingAsEmail = action.payload.email ?? null;
      saveActingAs(action.payload.clientId, action.payload.clientAccountId ?? null, action.payload.label, action.payload.email ?? null);
    },
    clearActingAs: (state) => {
      state.actingAsClientId = null;
      state.actingAsAccountId = null;
      state.actingAsLabel = null;
      state.actingAsEmail = null;
      clearActingAsStorage();
    },
    setRole: (state, action: PayloadAction<Role>) => {
      state.role = action.payload;
    },
    setSubscriptionPlan: (state, action: PayloadAction<string | null>) => {
      state.subscriptionPlan = action.payload;
    },
  },
});

export const fetchBrandRegister = createAction<BrandRegisterPayload>(
  "authUser/fetchBrandRegister",
);

export const fetchCompetitorsRegister = createAction<Company>(
  "authUser/fetchCompetitorsRegister",
);
export const registerUser = createAction<RegisterPayload>(
  "authUser/registerUser",
);
export const loginUser = createAction<LoginPayload>("authUser/loginUser");
export const authorizationUser = createAction<AuthorizationUserPayload>(
  "authUser/authorizationUser",
);
export const oAuthUser = createAction<OAuthPayload>("authUser/oAuthUser");
export const updateUser = createAction<UpdateUserPayload>(
  "authUser/updateUser",
);
export const fetchPaymentStatus = createAction<FetchPaymentStatusPayload>(
  "authUser/fetchPaymentStatus",
);
export const logoutUser = createAction("authUser/logoutUser");
export const getStripeCustomerPortal = createAction(
  "authUser/getStripeCustomerPortal",
);
export const forgotPasswordUser = createAction<ForgotPasswordPayload>(
  "authUser/forgotPasswordUser",
);
export const updatePasswordUser = createAction<UpdatePasswordPayload>(
  "authUser/updatePasswordUser",
);
export const resetPasswordUser = createAction<ResetPasswordPayload>(
  "authUser/resetPasswordUser",
);
export const fetchRelatedBrands = createAction<FetchRelatedBrandsPayload>(
  "authUser/fetchRelatedBrands",
);
export const refreshSession = createAction(
  "authUser/refreshSession",
);

export const upgradeSubscription = createAction("authUser/upgradeSubscription");

export const {
  setPlan,
  setRegisterUserLoading,
  setResetPasswordUserLoading,
  setRecovery,
  setBrand,
  registerUserSuccess,
  registerUserFailed,
  loginUserSuccess,
  loginUserFailed,
  resetPasswordUserSuccess,
  resetPasswordUserFailed,
  updatePasswordUserSuccess,
  updatePasswordUserFailed,
  updateUserSuccess,
  updateUserFailed,
  logoutUserSuccess,
  setUpdateUserLoading,
  fetchPaymentStatusSuccess,
  fetchPaymentStatusFailed,
  setPaymentStatusLoading,
  setLoginUserLoading,
  setUser,
  setStepRegister,
  setBrandRegisterLoading,
  setBrandRegisterSuccess,
  setBrandRegisterFailed,
  setCompetitorsRegister,
  fetchCompetitorsRegisterSuccess,
  fetchCompetitorsRegisterFailed,
  setSelectedCompetitorsRegister,
  setCompetitorsRegisterLoading,
  setRelatedBrandsLoading,
  fetchRelatedBrandsSuccess,
  fetchRelatedBrandsFailed,
  resetRelatedBrands,
  setMemberInfo,
  clearActingAsOnUserChange,
  setAgencyInfo,
  setActingAs,
  clearActingAs,
  setRole,
  setSubscriptionPlan,
} = authSlice.actions;

export default authSlice.reducer;
